import { Vue3ProgressPlugin } from "@marcoschulte/vue3-progress";
import VueDOMPurifyHTML from "vue-dompurify-html";
import VueTippy from "vue-tippy";
import { createPinia } from "pinia";
import { I18nInjectionKey } from "vue-i18n";

import { getRouter } from "@/router";
import { resyncRouterHistory } from "@/remote/resyncRouterHistory";
import { i18n } from "@/i18n";
import globalDerectives from "@/derectives";

import api from "@/plugins/api";
import userPrefs from "@/plugins/userPrefs";
import jsonToCsv from "@/plugins/jsonToCsv";

import { usePrimaryNavItems } from "@/composables";
import {
  trackAppcuesPageView,
  trackInitialAppcuesPageView,
} from "@/utils/appcues";

import {
  ClDataTable,
  ClFormLabel,
  ClFormInput,
  ClFormGroup,
  ClDataTableDeleteAction,
  ClDataTableEditAction,
  ClDataTableViewAction,
  ClButton,
  ClHeading,
  ClP,
  ClHr,
  ClPill,
  ClModal,
  ClSpinner,
  ClSidebar,
  ClSidebarNav,
  ClSidebarNavItemParent,
  ClSidebarNavItemChild,
  ClSidebarDropdown,
  ClSidebarDropdownSearch,
  ClSidebarDropdownMenuItem,
  ClDropdown,
  ClDropdownItem,
  ClTabs,
  ClTabsItem,
  ClBadge,
  ClBadgeLabel,
  ClEmailAttachment,
  ClSkeleton,
  ClSkeletonRow,
  ClFooter,
  ClHeaderDropdown,
  ClHeaderDropdownProduct,
  ClHeaderDropdownLink,
  ClToast,
  ClFormRadioGroup,
  ClFormTextarea,
  ClFormSelect,
  ClActiveFilter,
  ClStepper,
  ClToggle,
  ClTableWrapper,
  ClTable,
  ClThead,
  ClTbody,
  ClTr,
  ClTh,
  ClTd,
  ClTrSkeleton,
  ClThSorting,
  ClTablePageSize,
  ClTableBasicPagination,
  ClTablePaginationState,
  ClTableColumnVisibilityToggle,
  ClTableDataStateError,
  ClTableDataStateNoResults,
  ClTableDataStateNoData,
  ClInputCheckbox,
  ClInputSearch,
  ClInputRadio,
  ClAccordion,
  ClProgressBar,
  ClRadioButtons,
  ClRadioButton,
  ClMultiselect,
  ClTableActionButton,
  ClTableHeaderCell,
  ClTableColumnsConfigurationPopover,
  ClTableColumnsConfigurationPopoverItem,
  ClSelect,
  ClSelectItem,
  ClCallout,
  ClDataCard,
  ClDataCardFooterLoading,
  ClDataCardFooterBigNumber,
  ClDataCardFooterSmallNumber,
  ClRaisedTabSet,
  ClRaisedTab,
  ClChartCard,
  ClSearchableDropdown,
  ClBreadcrumbsV2,
  ClBreadcrumbsItem,
  CbNotificationBar,
  // Icons
  ClIconEmptyBox,
  ClIconEmptyFolder,
  ClIconEmptyEmail,
  ClIconEmptyDashboard,
  ClIconEmptyDonutChart,
  ClIconEmptyUfo,
  ClIconEmptyGraph,
  // CbIcons
  CbIconAdd,
  CbIconAddProducts,
  CbIconAi,
  CbIconAiTag,
  CbIconAlignLeft,
  CbIconAntiSpamProtection,
  CbIconArrow,
  CbIconArrowDropdown,
  CbIconArrowUp,
  CbIconAttentionCritical,
  CbIconCheckLegacy,
  CbIconChevronDown,
  CbIconChevronLeft,
  CbIconChevronRight,
  CbIconChevronUp,
  CbIconClose,
  CbIconClose2,
  CbIconCloseLegacy,
  CbIconColumns,
  CbIconCompanies,
  CbIconContract,
  CbIconCopy,
  CbIconCustomers,
  CbIconCustomiseDisplay,
  CbIconDarkMode,
  CbIconDashboard,
  CbIconDate,
  CbIconDelete,
  CbIconDnsFiltering,
  CbIconDomain,
  CbIconDomainAllow,
  CbIconDoubleCheck,
  CbIconDownload,
  CbIconEdit,
  CbIconEmail,
  CbIconEmailArchiving,
  CbIconExternalLink,
  CbIconEyeHidden,
  CbIconFile,
  CbIconFilter,
  CbIconFilterLegacy,
  CbIconHistory,
  CbIconHome,
  CbIconImage,
  CbIconInformation,
  CbIconInProgress,
  CbIconInsight,
  CbIconLicense,
  CbIconLightMode,
  CbIconLock,
  CbIconLogOut,
  CbIconMailLock,
  CbIconMailSend,
  CbIconMenu,
  CbIconNotifications,
  CbIconOverview,
  CbIconPartnerPortal,
  CbIconPhishing,
  CbIconPlusLegacy,
  CbIconProducts,
  CbIconQuarantine,
  CbIconReorder,
  CbIconReorder2,
  CbIconReportManager,
  CbIconReporting,
  CbIconRestore,
  CbIconScheduler,
  CbIconSearch,
  CbIconSearchLegacy,
  CbIconSenderAllow,
  CbIconSettings,
  CbIconSort,
  CbIconSpy,
  CbIconSuccess,
  CbIconSupport,
  CbIconSystemMode,
  CbIconTable,
  CbIconTick,
  CbIconTraining,
  CbIconUnlock,
  CbIconUpload,
  CbIconUser,
  CbIconView,
  CbIconWarning,
} from "@titanhq/frontend-component-library";

// Modules
import {
  PhishTitanModule,
  LicenceUsageModule,
  SettingsModule,
  PhishingSimulationsModule,
  ArcTitanModule,
  SecurityAwarnessTrainingModule,
  DashboarPlatFormdModule,
  ReportManagerModule,
} from "@/modules/index";

import type { App as VueApp } from "vue";
import { withRemoteScope } from "@/remote/modalScope";

/**
 * A feature module's registration function. Modules receive the app for
 * symmetry, but none currently use it — they add routes and nav items, both of
 * which are global rather than per-app (hence `installPageGlobals`).
 */
type FeatureModule = (context: {
  app: VueApp;
  router: ReturnType<typeof getRouter>;
  registerNavItems: ReturnType<typeof usePrimaryNavItems>["registerNavItems"];
}) => void;

/**
 * Shared application configuration — the single source of truth for BOTH entry
 * points: the standalone `src/main.js` and the federated custom-element entry.
 *
 * Split out of `main.js`, which configured and mounted at module scope and so
 * could not be reused. Behaviour is unchanged for standalone; the ordering below
 * is the ordering main.js had, and it is load-bearing (see `configureApp`).
 */

/**
 * ONE Pinia for the whole page, not one per app.
 *
 * Each `defineCustomElement` instance creates its own Vue app and therefore
 * calls `configureApp` again. `createPinia()` inline (as main.js had it) would
 * hand each element a separate store registry: the app element and the nav
 * element would then diverge silently — the nav would never observe the
 * customer selected in the app. Module-scoped, so every app shares one.
 */
export const sharedPinia = createPinia();

/**
 * Guards the work that is global rather than per-app: routes and nav items live
 * on the router / module-scoped state. Running these again for a second custom
 * element would double-register routes.
 */
let globalsInstalled = false;

function installPageGlobals(app: VueApp): void {
  if (globalsInstalled) return;
  globalsInstalled = true;

  // Appcues is script-tagged from this repo's index.html only. A host does not
  // load it, so an unguarded `window.Appcues.page()` would throw on EVERY
  // navigation once embedded — the helper no-ops when Appcues is absent.
  getRouter().afterEach(() => {
    trackAppcuesPageView();
  });
  getRouter()
    .isReady()
    .then(() =>
      trackInitialAppcuesPageView({
        isPageReady: () =>
          Boolean(document.querySelector('[data-test-id="side-nav"]')),
      }),
    )
    .catch(() => {});

  // Pinia must be active before usePrimaryNavItems()/module registration, since
  // those read the Pinia authentication store.
  const { registerNavItems } = usePrimaryNavItems();

  function registerModule(module: FeatureModule) {
    module({ app, router: getRouter(), registerNavItems });
  }

  registerModule(LicenceUsageModule);
  registerModule(SettingsModule);
  registerModule(PhishingSimulationsModule);
  registerModule(PhishTitanModule);
  registerModule(ArcTitanModule);
  registerModule(SecurityAwarnessTrainingModule);
  registerModule(DashboarPlatFormdModule);
  registerModule(ReportManagerModule);
}

/**
 * Configure one Vue app: shared Pinia first (module registration depends on an
 * active Pinia), then the page-global side effects, then this app's plugins and
 * component registrations.
 */
export function configureApp(app: VueApp): VueApp {
  app.use(sharedPinia);

  installPageGlobals(app);

  app.use(i18n);
  // ALSO provide the global key, not only `use()`. When this app is mounted as a
  // custom element (see remote/elements.ts), Vue sets `isCE` on the element's
  // ROOT instance — App.vue — and vue-i18n's getI18nInstance() then resolves
  // through the global `I18nInjectionKey` instead of the per-app
  // `__VUE_I18N_SYMBOL__` that `use()` provides. Without this, `useI18n()` in
  // App.vue throws NOT_INSTALLED_WITH_PROVIDE ("Need to install with `provide`
  // function"), the mount aborts and the embedded element renders empty — while
  // standalone, where App.vue is an ordinary root component, is unaffected and so
  // shows nothing.
  //
  // `use()` stays: descendant components take the `__VUE_I18N_SYMBOL__` branch
  // (`isCE` is set on the element root only), and standalone needs it.
  app.provide(I18nInjectionKey, i18n);
  const router = getRouter();
  resyncRouterHistory(router);
  app.use(router);
  app.use(VueDOMPurifyHTML);
  app.use(userPrefs);
  app.use(api);
  app.use(jsonToCsv);
  app.use(Vue3ProgressPlugin);

  app.use(globalDerectives);

  app.use(
    VueTippy,
    // optional
    {
      directive: "tippy", // => v-tippy
      component: "tippy", // => <tippy/>
      componentSingleton: "tippy-singleton", // => <tippy-singleton/>,
      defaultProps: {
        placement: "auto-end",
        allowHTML: true,
      }, // => Global default options * see all props
    },
  );

  app.component("ClDataTable", ClDataTable);
  app.component("ClFormLabel", ClFormLabel);
  app.component("ClFormTextarea", ClFormTextarea);
  app.component("ClFormInput", ClFormInput);
  app.component("ClFormGroup", ClFormGroup);
  app.component("ClDataTableEditAction", ClDataTableEditAction);
  app.component("ClDataTableDeleteAction", ClDataTableDeleteAction);
  app.component("ClDataTableViewAction", ClDataTableViewAction);
  app.component("ClButton", ClButton);
  app.component("ClHeading", ClHeading);
  app.component("ClP", ClP);
  app.component("ClHr", ClHr);
  app.component("ClPill", ClPill);
  app.component("ClModal", withRemoteScope(ClModal));
  app.component("ClSpinner", ClSpinner);
  app.component("ClSidebar", ClSidebar);
  app.component("ClSidebarNav", ClSidebarNav);
  app.component("ClSidebarNavItemParent", ClSidebarNavItemParent);
  app.component("ClSidebarNavItemChild", ClSidebarNavItemChild);
  app.component("ClSidebarDropdown", ClSidebarDropdown);
  app.component("ClSidebarDropdownSearch", ClSidebarDropdownSearch);
  app.component("ClSidebarDropdownMenuItem", ClSidebarDropdownMenuItem);
  app.component("ClDropdown", ClDropdown);
  app.component("ClDropdownItem", ClDropdownItem);
  app.component("ClTabs", ClTabs);
  app.component("ClTabsItem", ClTabsItem);
  app.component("ClBadge", ClBadge);
  app.component("ClBadgeLabel", ClBadgeLabel);
  app.component("ClEmailAttachment", ClEmailAttachment);
  app.component("ClSkeleton", ClSkeleton);
  app.component("ClSkeletonRow", ClSkeletonRow);
  app.component("ClFooter", ClFooter);
  app.component("ClHeaderDropdown", ClHeaderDropdown);
  app.component("ClHeaderDropdownProduct", ClHeaderDropdownProduct);
  app.component("ClHeaderDropdownLink", ClHeaderDropdownLink);
  app.component("ClToast", ClToast);
  app.component("ClFormRadioGroup", ClFormRadioGroup);
  app.component("ClFormSelect", ClFormSelect);
  app.component("ClActiveFilter", ClActiveFilter);
  app.component("ClStepper", ClStepper);
  app.component("ClToggle", ClToggle);
  app.component("ClInputCheckbox", ClInputCheckbox);
  app.component("ClInputSearch", ClInputSearch);
  app.component("ClTableWrapper", ClTableWrapper);
  app.component("ClTable", ClTable);
  app.component("ClThead", ClThead);
  app.component("ClTbody", ClTbody);
  app.component("ClTr", ClTr);
  app.component("ClTh", ClTh);
  app.component("ClTd", ClTd);
  app.component("ClTableDataStateError", ClTableDataStateError);
  app.component("ClTableDataStateNoResults", ClTableDataStateNoResults);
  app.component("ClTableDataStateNoData", ClTableDataStateNoData);
  app.component("ClTrSkeleton", ClTrSkeleton);
  app.component("ClThSorting", ClThSorting);
  app.component("ClTablePageSize", ClTablePageSize);
  app.component("ClTableBasicPagination", ClTableBasicPagination);
  app.component("ClTablePaginationState", ClTablePaginationState);
  app.component("ClInputRadio", ClInputRadio);
  app.component("ClTableColumnVisibilityToggle", ClTableColumnVisibilityToggle);
  app.component("ClAccordion", ClAccordion);
  app.component("ClProgressBar", ClProgressBar);
  app.component("ClDataCard", ClDataCard);
  app.component("ClDataCardFooterLoading", ClDataCardFooterLoading);
  app.component("ClDataCardFooterBigNumber", ClDataCardFooterBigNumber);
  app.component("ClDataCardFooterSmallNumber", ClDataCardFooterSmallNumber);
  app.component("ClRaisedTabSet", ClRaisedTabSet);
  app.component("ClRaisedTab", ClRaisedTab);
  app.component("ClChartCard", ClChartCard);
  app.component("ClSearchableDropdown", ClSearchableDropdown);
  app.component("ClBreadcrumbsV2", ClBreadcrumbsV2);
  app.component("ClBreadcrumbsItem", ClBreadcrumbsItem);
  app.component("ClRadioButtons", ClRadioButtons);
  app.component("ClRadioButton", ClRadioButton);
  app.component("ClTableHeaderCell", ClTableHeaderCell);
  app.component("ClTableActionButton", ClTableActionButton);
  app.component(
    "ClTableColumnsConfigurationPopover",
    ClTableColumnsConfigurationPopover,
  );
  app.component(
    "ClTableColumnsConfigurationPopoverItem",
    ClTableColumnsConfigurationPopoverItem,
  );
  app.component("ClSelect", ClSelect);
  app.component("ClSelectItem", ClSelectItem);
  app.component("ClCallout", ClCallout);
  app.component("ClMultiselect", ClMultiselect);
  app.component("CbNotificationBar", CbNotificationBar);

  // Icons
  app.component("ClIconEmptyBox", ClIconEmptyBox);
  app.component("ClIconEmptyFolder", ClIconEmptyFolder);
  app.component("ClIconEmptyEmail", ClIconEmptyEmail);
  app.component("ClIconEmptyDashboard", ClIconEmptyDashboard);
  app.component("ClIconEmptyDonutChart", ClIconEmptyDonutChart);
  app.component("ClIconEmptyUfo", ClIconEmptyUfo);
  app.component("ClIconEmptyGraph", ClIconEmptyGraph);

  // CbIcons
  app.component("cb-icon-add", CbIconAdd);
  app.component("cb-icon-add-products", CbIconAddProducts);
  app.component("cb-icon-ai", CbIconAi);
  app.component("cb-icon-ai-tag", CbIconAiTag);
  app.component("cb-icon-align-left", CbIconAlignLeft);
  app.component("cb-icon-anti-spam-protection", CbIconAntiSpamProtection);
  app.component("cb-icon-arrow", CbIconArrow);
  app.component("cb-icon-arrow-dropdown", CbIconArrowDropdown);
  app.component("cb-icon-arrow-up", CbIconArrowUp);
  app.component("cb-icon-attention-critical", CbIconAttentionCritical);
  app.component("cb-icon-check-legacy", CbIconCheckLegacy);
  app.component("cb-icon-chevron-down", CbIconChevronDown);
  app.component("cb-icon-chevron-left", CbIconChevronLeft);
  app.component("cb-icon-chevron-right", CbIconChevronRight);
  app.component("cb-icon-chevron-up", CbIconChevronUp);
  app.component("cb-icon-close", CbIconClose);
  app.component("cb-icon-close-2", CbIconClose2);
  app.component("cb-icon-close-legacy", CbIconCloseLegacy);
  app.component("cb-icon-columns", CbIconColumns);
  app.component("cb-icon-companies", CbIconCompanies);
  app.component("cb-icon-contract", CbIconContract);
  app.component("cb-icon-copy", CbIconCopy);
  app.component("cb-icon-customers", CbIconCustomers);
  app.component("cb-icon-customise-display", CbIconCustomiseDisplay);
  app.component("cb-icon-dark-mode", CbIconDarkMode);
  app.component("cb-icon-dashboard", CbIconDashboard);
  app.component("cb-icon-date", CbIconDate);
  app.component("cb-icon-delete", CbIconDelete);
  app.component("cb-icon-dns-filtering", CbIconDnsFiltering);
  app.component("cb-icon-domain", CbIconDomain);
  app.component("cb-icon-domain-allow", CbIconDomainAllow);
  app.component("cb-icon-double-check", CbIconDoubleCheck);
  app.component("cb-icon-download", CbIconDownload);
  app.component("cb-icon-edit", CbIconEdit);
  app.component("cb-icon-email", CbIconEmail);
  app.component("cb-icon-email-archiving", CbIconEmailArchiving);
  app.component("cb-icon-external-link", CbIconExternalLink);
  app.component("cb-icon-eye-hidden", CbIconEyeHidden);
  app.component("cb-icon-file", CbIconFile);
  app.component("cb-icon-filter", CbIconFilter);
  app.component("cb-icon-filter-legacy", CbIconFilterLegacy);
  app.component("cb-icon-history", CbIconHistory);
  app.component("cb-icon-home", CbIconHome);
  app.component("cb-icon-image", CbIconImage);
  app.component("cb-icon-information", CbIconInformation);
  app.component("cb-icon-in-progress", CbIconInProgress);
  app.component("cb-icon-insight", CbIconInsight);
  app.component("cb-icon-license", CbIconLicense);
  app.component("cb-icon-light-mode", CbIconLightMode);
  app.component("cb-icon-lock", CbIconLock);
  app.component("cb-icon-log-out", CbIconLogOut);
  app.component("cb-icon-mail-lock", CbIconMailLock);
  app.component("cb-icon-mail-send", CbIconMailSend);
  app.component("cb-icon-menu", CbIconMenu);
  app.component("cb-icon-notifications", CbIconNotifications);
  app.component("cb-icon-overview", CbIconOverview);
  app.component("cb-icon-partner-portal", CbIconPartnerPortal);
  app.component("cb-icon-phishing", CbIconPhishing);
  app.component("cb-icon-plus-legacy", CbIconPlusLegacy);
  app.component("cb-icon-products", CbIconProducts);
  app.component("cb-icon-quarantine", CbIconQuarantine);
  app.component("cb-icon-reorder", CbIconReorder);
  app.component("cb-icon-reorder-2", CbIconReorder2);
  app.component("cb-icon-report-manager", CbIconReportManager);
  app.component("cb-icon-reporting", CbIconReporting);
  app.component("cb-icon-restore", CbIconRestore);
  app.component("cb-icon-scheduler", CbIconScheduler);
  app.component("cb-icon-search", CbIconSearch);
  app.component("cb-icon-search-legacy", CbIconSearchLegacy);
  app.component("cb-icon-sender-allow", CbIconSenderAllow);
  app.component("cb-icon-settings", CbIconSettings);
  app.component("cb-icon-sort", CbIconSort);
  app.component("cb-icon-spy", CbIconSpy);
  app.component("cb-icon-success", CbIconSuccess);
  app.component("cb-icon-support", CbIconSupport);
  app.component("cb-icon-system-mode", CbIconSystemMode);
  app.component("cb-icon-table", CbIconTable);
  app.component("cb-icon-tick", CbIconTick);
  app.component("cb-icon-training", CbIconTraining);
  app.component("cb-icon-unlock", CbIconUnlock);
  app.component("cb-icon-upload", CbIconUpload);
  app.component("cb-icon-user", CbIconUser);
  app.component("cb-icon-view", CbIconView);
  app.component("cb-icon-warning", CbIconWarning);

  return app;
}

export default { configureApp, sharedPinia };
